/**
 * SwipeableSidebarItem — horizontal swipe gestures for sidebar items (mobile only).
 *
 * - R→L short (15%): Toggle multi-select (primary color reveal)
 * - R→L long  (60%): Open context menu (gray reveal with ⋯ icon)
 *
 * Desktop: renders children directly (no gesture wrapper).
 * Coordinates with MobileDrawer via `sidebarSwipeActive` ref to prevent
 * drawer-close gesture from firing during item swipe.
 */

import { type ReactNode, useRef, useState, useCallback } from "react"
import { useDrag } from "@use-gesture/react"
import { cn } from "@/shared/utils/general/cn"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import { hapticLight, hapticMedium } from "@/shared/utils/platform/haptics"
import MoreIcon from "@/shared/icons/totodoicons/stroke/three-dots-horizontal.svg?react"
import SelectIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"

// Exported ref — read by MobileDrawer to block close gesture during item swipe
export let sidebarSwipeActive = false

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SHORT_THRESHOLD = 0.15
const LONG_THRESHOLD = 0.60
const ACTIVATION_PX = 8
const VELOCITY_MIN = 0.3

const COLOR_SELECT = "var(--primary)"
const COLOR_CONTEXT = "#6b7280"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SidebarSwipeAction = "select" | "contextMenu"

interface SwipeableSidebarItemProps {
  children: ReactNode
  /** Called when a swipe action is committed */
  onAction?: (action: SidebarSwipeAction) => void
  /** Disable swipe (e.g. during DnD) */
  disabled?: boolean
  /** Border radius class — connected selection rounding (default: rounded-lg) */
  borderRadiusClass?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SwipeableSidebarItem({
  children,
  onAction,
  disabled,
  borderRadiusClass = "rounded-lg",
}: SwipeableSidebarItemProps) {
  const { isMobile } = useBreakpoint()
  const rowRef = useRef<HTMLDivElement>(null)
  const [offsetX, setOffsetX] = useState(0)
  const [bgColor, setBgColor] = useState("transparent")
  const [isTransitioning, setIsTransitioning] = useState(false)

  const hapticShortRef = useRef(false)
  const hapticLongRef = useRef(false)

  const getRowWidth = useCallback(
    () => rowRef.current?.offsetWidth ?? 300,
    [],
  )

  const computeAction = useCallback(
    (mx: number, rowWidth: number): SidebarSwipeAction | null => {
      const pct = Math.abs(mx) / rowWidth
      if (pct < SHORT_THRESHOLD) return null
      // L→R (positive) = select
      if (mx > 0) return "select"
      // R→L (negative) = contextMenu
      return "contextMenu"
    },
    [],
  )

  const computeBgColor = useCallback(
    (mx: number, rowWidth: number): string => {
      const pct = Math.abs(mx) / rowWidth
      if (pct < SHORT_THRESHOLD) return "transparent"
      // L→R = primary (select), R→L = gray (context menu)
      return mx > 0 ? COLOR_SELECT : COLOR_CONTEXT
    },
    [],
  )

  const bind = useDrag(
    ({ movement: [mx], velocity: [vx], last, cancel, memo }) => {
      if (disabled || !isMobile) {
        cancel?.()
        return memo
      }

      const rowWidth = getRowWidth()
      const pct = Math.abs(mx) / rowWidth

      if (!last) {
        sidebarSwipeActive = true

        if (pct >= SHORT_THRESHOLD && !hapticShortRef.current) {
          hapticShortRef.current = true
          hapticLight()
        }
        if (pct >= LONG_THRESHOLD && !hapticLongRef.current) {
          hapticLongRef.current = true
          hapticMedium()
        }

        // Dampening past long threshold
        let dampened = mx
        if (pct > LONG_THRESHOLD) {
          const excess = Math.abs(mx) - rowWidth * LONG_THRESHOLD
          dampened = Math.sign(mx) * (rowWidth * LONG_THRESHOLD + excess * 0.3)
        }

        setOffsetX(dampened)
        setBgColor(computeBgColor(mx, rowWidth))
        return memo
      }

      // Released
      sidebarSwipeActive = false
      hapticShortRef.current = false
      hapticLongRef.current = false

      let action = computeAction(mx, rowWidth)

      // Velocity boost — fast swipe past halfway to short threshold counts
      if (!action && vx > VELOCITY_MIN && pct > SHORT_THRESHOLD * 0.5) {
        action = mx > 0 ? "select" : "contextMenu"
      }

      if (action && onAction) {
        hapticMedium()
        onAction(action)
        // Prevent click after swipe
        const stop = (e: Event) => { e.stopPropagation(); e.preventDefault() }
        document.addEventListener("click", stop, { capture: true, once: true })
        setTimeout(() => document.removeEventListener("click", stop, { capture: true }), 400)
      }

      // Spring back
      setIsTransitioning(true)
      setOffsetX(0)
      setBgColor("transparent")
      requestAnimationFrame(() => {
        setTimeout(() => setIsTransitioning(false), 300)
      })

      return memo
    },
    {
      axis: "x",
      filterTaps: true,
      pointer: { touch: true },
      threshold: ACTIVATION_PX,
    },
  )

  // Desktop: no gestures — still wrap with sidebar-matching bg for consistency
  if (!isMobile) {
    return (
      <div className={cn("relative overflow-hidden", borderRadiusClass)}>
        <div className={cn("relative z-10 bg-[var(--sidebar-surface,var(--sidebar))]", borderRadiusClass)}>
          {children}
        </div>
      </div>
    )
  }

  const isSwiping = offsetX !== 0

  return (
    <div
      ref={rowRef}
      className={cn("relative overflow-hidden", borderRadiusClass)}
      style={{ touchAction: "pan-y" }}
    >
      {/* Reveal background — colored surface with icon that appears as row slides */}
      <div
        className={cn(
          "absolute inset-0 flex items-center",
          borderRadiusClass,
          offsetX > 0 ? "justify-start pl-4" : "justify-end pr-4",
        )}
        style={{ backgroundColor: bgColor }}
        aria-hidden
      >
        {Math.abs(offsetX) > getRowWidth() * SHORT_THRESHOLD && (
          <span className="flex items-center justify-center text-white">
            {offsetX > 0 ? (
              <SelectIcon width={22} height={22} />
            ) : (
              <MoreIcon width={22} height={22} />
            )}
          </span>
        )}
      </div>

      {/* Foreground card — slides with swipe, opaque bg makes reveal visible */}
      <div
        {...bind()}
        className={cn("relative z-10 bg-[var(--sidebar-surface,var(--sidebar))]", borderRadiusClass)}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: isTransitioning
            ? "transform 300ms cubic-bezier(0.2, 0, 0, 1)"
            : "none",
        }}
        {...(isSwiping ? { "data-swiping": "" } : {})}
      >
        {children}
      </div>
    </div>
  )
}
